import * as Yup from 'yup';
import { parseDateLocal } from '@packages/core';
import { isFieldFilled } from './coerce';

type TestBuilder = (
  schema: Yup.AnySchema,
  params: Record<string, unknown> | undefined,
  message: string,
) => Yup.AnySchema;

const TEST_REGISTRY: Record<string, TestBuilder> = {
  maxDecimalPlaces: (schema, params, message) => {
    const places = typeof params?.places === 'number' ? params.places : 4;
    return schema.test('max-decimal-places', message, (value) => {
      if (value === null || value === undefined || value === '') return true;
      const str = String(value);
      const parts = str.split('.');
      return parts.length < 2 || (parts[1]?.length ?? 0) <= places;
    });
  },
  exactDecimalPlaces: (schema, params, message) => {
    const places = typeof params?.places === 'number' ? params.places : 4;
    return schema.test('exact-decimal-places', message, (value) => {
      if (value === null || value === undefined || value === '') return true;
      const str = String(value);
      const parts = str.split('.');
      return parts.length === 2 && parts[1].length === places;
    });
  },
  greaterThanField: (schema, params, message) =>
    schema.test('greater-than-field', message, function (value) {
      const field = params?.field;
      if (typeof field !== 'string') return true;
      const other = (this.parent as Record<string, unknown>)[field];
      if (!isFieldFilled(value) || !isFieldFilled(other)) return true;
      return Number(value) > Number(other);
    }),
  greaterThanOrEqualField: (schema, params, message) =>
    schema.test('greater-than-or-equal-field', message, function (value) {
      const field = params?.field;
      if (typeof field !== 'string') return true;
      const other = (this.parent as Record<string, unknown>)[field];
      if (!isFieldFilled(value) || !isFieldFilled(other)) return true;
      return Number(value) >= Number(other);
    }),
  lessThanField: (schema, params, message) =>
    schema.test('less-than-field', message, function (value) {
      const field = params?.field;
      if (typeof field !== 'string') return true;
      const other = (this.parent as Record<string, unknown>)[field];
      if (!isFieldFilled(value) || !isFieldFilled(other)) return true;
      return Number(value) < Number(other);
    }),
  lessThanOrEqualField: (schema, params, message) =>
    schema.test('less-than-or-equal-field', message, function (value) {
      const field = params?.field;
      if (typeof field !== 'string') return true;
      const other = (this.parent as Record<string, unknown>)[field];
      if (!isFieldFilled(value) || !isFieldFilled(other)) return true;
      return Number(value) <= Number(other);
    }),
  equalToField: (schema, params, message) =>
    schema.test('equal-to-field', message, function (value) {
      const field = params?.field;
      if (typeof field !== 'string') return true;
      const other = (this.parent as Record<string, unknown>)[field];
      if (!isFieldFilled(value) && !isFieldFilled(other)) return true;
      return value === other;
    }),
  notEqualToField: (schema, params, message) =>
    schema.test('not-equal-to-field', message, function (value) {
      const field = params?.field;
      if (typeof field !== 'string') return true;
      const other = (this.parent as Record<string, unknown>)[field];
      if (!isFieldFilled(value) || !isFieldFilled(other)) return true;
      return value !== other;
    }),
  greaterThanZero: (schema, _params, message) =>
    schema.test('greater-than-zero', message, (value) => {
      if (value === null || value === undefined || value === '') return true;
      const numValue = Number(value);
      return !Number.isNaN(numValue) && numValue > 0;
    }),
  greaterThanOrEqualZero: (schema, _params, message) =>
    schema.test('greater-than-or-equal-zero', message, (value) => {
      if (value === null || value === undefined || value === '') return true;
      const numValue = Number(value);
      return !Number.isNaN(numValue) && numValue >= 0;
    }),
  digitsOnly: (schema, _params, message) =>
    schema.test('digits-only', message, (value) => {
      if (value === null || value === undefined || value === '') return true;
      return /^\d+$/.test(String(value));
    }),
  requiredWhenAnyOf: (schema, params, message) =>
    schema.test('required-when-any-of', message, function (value) {
      const fields = params?.fields;
      if (!Array.isArray(fields)) return true;
      const parent = this.parent as Record<string, unknown>;
      const hasOtherValues = fields.some((field) =>
        typeof field === 'string' ? isFieldFilled(parent[field]) : false,
      );
      if (!hasOtherValues) return true;
      return isFieldFilled(value);
    }),
  dateAfterField: (schema, params, message) =>
    schema.test('date-after-field', message, function (value) {
      const field = params?.field;
      if (typeof field !== 'string') return true;
      const other = (this.parent as Record<string, unknown>)[field];
      if (!isFieldFilled(value) || !isFieldFilled(other)) return true;
      return parseDateLocal(String(value)).getTime() > parseDateLocal(String(other)).getTime();
    }),
  dateBeforeField: (schema, params, message) =>
    schema.test('date-before-field', message, function (value) {
      const field = params?.field;
      if (typeof field !== 'string') return true;
      const other = (this.parent as Record<string, unknown>)[field];
      if (!isFieldFilled(value) || !isFieldFilled(other)) return true;
      return parseDateLocal(String(value)).getTime() < parseDateLocal(String(other)).getTime();
    }),
  dateAfterOrEqualField: (schema, params, message) =>
    schema.test('date-after-or-equal-field', message, function (value) {
      const field = params?.field;
      if (typeof field !== 'string') return true;
      const other = (this.parent as Record<string, unknown>)[field];
      if (!isFieldFilled(value) || !isFieldFilled(other)) return true;
      return parseDateLocal(String(value)).getTime() >= parseDateLocal(String(other)).getTime();
    }),
  dateBeforeOrEqualField: (schema, params, message) =>
    schema.test('date-before-or-equal-field', message, function (value) {
      const field = params?.field;
      if (typeof field !== 'string') return true;
      const other = (this.parent as Record<string, unknown>)[field];
      if (!isFieldFilled(value) || !isFieldFilled(other)) return true;
      return parseDateLocal(String(value)).getTime() <= parseDateLocal(String(other)).getTime();
    }),
};

export function applyBuiltInTest(
  schema: Yup.AnySchema,
  name: string,
  params: Record<string, unknown> | undefined,
  message: string,
): Yup.AnySchema {
  const builder = TEST_REGISTRY[name];
  if (!builder) return schema;
  return builder(schema, params, message);
}

export function isKnownBuiltInTest(name: string): boolean {
  return name in TEST_REGISTRY;
}
